import { QUESTIONS } from '../discovery/questions';
import { THEMES, type DiscoveryPlace } from '../discovery/types';
import { createElement, setText } from './dom';
import { discoverySource } from './discoverySource';

function elements(parent: HTMLElement) {
  const root = createElement('section', 'discovery-questions', parent);
  root.dataset.theme = 'dark';
  setText(createElement('h3', '', root), 'Think like a historian');
  const disclosure = createElement('details', '', root);
  setText(createElement('summary', '', disclosure), 'Try a question');
  setText(
    createElement('p', 'discovery-question-hint', disclosure),
    'Choose an answer, then see what the sources tell us.'
  );
  const position = createElement('p', 'discovery-question-position', disclosure);
  const field = createElement('fieldset', '', disclosure);
  const legend = createElement('legend', '', field);
  legend.tabIndex = -1;
  const choices = createElement('div', 'discovery-question-choices', field);
  const feedback = createElement('div', 'discovery-question-feedback', disclosure);
  feedback.setAttribute('role', 'status');
  feedback.setAttribute('aria-atomic', 'true');
  const evidence = createElement('div', 'discovery-question-evidence', disclosure);
  const next = createElement('button', 'discovery-question-next', disclosure);
  next.type = 'button';
  return { position, legend, choices, feedback, evidence, next };
}

/**
 * Visitors can practise without an account because answers never change discoveries.
 */
export function createDiscoveryQuestions(
  parent: HTMLElement,
  places: readonly DiscoveryPlace[]
): void {
  const questions = QUESTIONS.map((question) => {
    const place = places.find((one) => one.id === question.place);
    if (!place || !place.text.includes(question.evidence) || place.sources.length === 0)
      throw new Error(`Question without discovery evidence: ${question.place}`);
    return { ...question, place };
  });
  const ui = elements(parent);
  let index = 0;
  let solved = false;
  const draw = (): void => {
    const question = questions[index];
    if (!question) throw new Error(`Unknown discovery question: ${index}`);
    solved = false;
    ui.position.textContent = `${index + 1} / ${questions.length} · `;
    setText(createElement('span', '', ui.position), THEMES[question.place.theme]);
    setText(ui.legend, question.prompt.en);
    ui.choices.replaceChildren();
    ui.feedback.replaceChildren();
    ui.evidence.replaceChildren();
    ui.evidence.hidden = true;
    ui.next.hidden = true;
    setText(ui.next, index === questions.length - 1 ? 'Start again' : 'Next question');
    for (const [choiceIndex, choice] of question.choices.entries()) {
      const button = createElement('button', '', ui.choices);
      button.type = 'button';
      button.setAttribute('aria-pressed', 'false');
      setText(button, choice.en);
      button.addEventListener('click', () => {
        if (solved) return;
        solved = choiceIndex === question.answer;
        answer(ui, question, button, solved);
      });
    }
    setText(createElement('h4', '', ui.evidence), 'Evidence from this place');
    setText(createElement('p', 'discovery-question-place', ui.evidence), question.place.name);
    setText(createElement('p', '', ui.evidence), question.place.text);
    discoverySource(ui.evidence, question.place, 'h4');
  };
  ui.next.addEventListener('click', () => {
    index = (index + 1) % questions.length;
    draw();
    ui.legend.focus();
  });
  draw();
}

function answer(
  ui: ReturnType<typeof elements>,
  question: { readonly explanation: { readonly en: string } },
  button: HTMLButtonElement,
  correct: boolean
): void {
  for (const choice of ui.choices.querySelectorAll('button')) {
    choice.setAttribute('aria-pressed', String(choice === button));
    choice.setAttribute('aria-disabled', String(correct));
  }
  ui.feedback.replaceChildren();
  setText(
    createElement('p', 'discovery-question-result', ui.feedback),
    correct ? 'That fits the evidence.' : 'Look at the evidence and try again.'
  );
  setText(createElement('p', '', ui.feedback), question.explanation.en);
  ui.evidence.hidden = false;
  ui.next.hidden = !correct;
}
